import React, { useEffect, useRef, useState } from 'react';
import { ImageIcon, Sparkles, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

/**
 * Build a Cloudinary poster URL pointing at a specific timestamp.
 * Falls back to the auto-frame variant when offset is null/undefined.
 */
const buildCloudinaryThumb = (videoUrl, offsetSec) => {
  if (!videoUrl || !videoUrl.includes('/video/upload/')) return '';
  const so = offsetSec == null ? 'so_auto' : `so_${offsetSec.toFixed(2)}`;
  return videoUrl
    .replace('/video/upload/', `/video/upload/${so},w_800,c_fill,f_jpg,q_auto/`)
    .replace(/\.(mp4|mov|webm|m4v|avi)(\?.*)?$/i, '.jpg$2');
};

const formatTime = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const VideoThumbnailPicker = ({ open, onOpenChange, video, onSave }) => {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const { toast } = useToast();

  // Reset state when reopening
  useEffect(() => {
    if (!open) return;
    setCurrentTime(0);
    setDuration(0);
  }, [open, video?.url]);

  const onLoaded = () => {
    if (videoRef.current) {
      const d = videoRef.current.duration || 0;
      setDuration(d);
      // Default scrubber to middle of the clip
      const middle = Math.min(Math.max(d / 2, 0), Math.max(d - 0.1, 0));
      videoRef.current.currentTime = middle;
      setCurrentTime(middle);
    }
  };

  const onSeek = (e) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };

  const handleUseAutoFrame = () => {
    onSave('');
    toast({ title: 'Reverted to auto-frame thumbnail' });
    onOpenChange(false);
  };

  const handleSaveFrame = () => {
    const thumb = buildCloudinaryThumb(video.url, currentTime);
    if (!thumb) {
      toast({
        title: 'Cannot pick frame',
        description: 'Only Cloudinary-hosted videos support frame thumbnails.',
        variant: 'destructive',
      });
      return;
    }
    onSave(thumb);
    toast({ title: `Thumbnail set to ${formatTime(currentTime)}` });
    onOpenChange(false);
  };

  if (!video) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2A2420] border-[#C5A880]/20 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-[#EADBC8] flex items-center gap-2">
            <ImageIcon size={18} className="text-[#C5A880]" />
            Pick a frame as thumbnail
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative bg-[#1A1612] rounded overflow-hidden">
            <video
              ref={videoRef}
              src={video.url}
              onLoadedMetadata={onLoaded}
              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
              className="w-full max-h-[55vh] object-contain bg-black"
              preload="auto"
              muted
              playsInline
              data-testid="thumb-picker-video"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-[#EADBC8] text-sm">
              <span className="font-mono text-[#C5A880]" data-testid="thumb-picker-time">
                {formatTime(currentTime)}
              </span>
              <span className="text-[#EADBC8]/60 font-mono">{formatTime(duration)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.05}
              value={currentTime}
              onChange={onSeek}
              disabled={!duration}
              className="w-full accent-[#C5A880] cursor-pointer disabled:opacity-40"
              data-testid="thumb-picker-slider"
            />
            <p className="text-[#EADBC8]/50 text-xs">
              Drag the slider to find the perfect frame, then save.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleUseAutoFrame}
            className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
            data-testid="thumb-picker-auto-btn"
          >
            <Sparkles size={14} className="mr-2" />
            Use auto-frame
          </Button>
          <div className="flex gap-2 sm:ml-auto">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#C5A880]/20 text-[#EADBC8]/70"
              data-testid="thumb-picker-cancel-btn"
            >
              <X size={14} className="mr-2" />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveFrame}
              disabled={!duration}
              className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
              data-testid="thumb-picker-save-btn"
            >
              Save frame as thumbnail
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
